/**
 * Website Builder — Super Admin Phase 1.
 *
 * A 3-column visual composer for the storefront homepage. Persists to the
 * SAME backend key that the legacy HomepageLayout page used
 * (`settings.commerce.homepage.sections`), so switching pages does NOT
 * require any backend change. Sections gain optional visual overrides
 * (title/subtitle/bg/padding) that are stored as free-form JSON on each
 * section object.
 *
 * Layout:
 *   ┌────────────────────────────────────────────────────────────────┐
 *   │ Sticky Action Bar  · Undo · Redo · Device · Preview · Publish  │
 *   ├──────────┬────────────────────────────────────┬────────────────┤
 *   │ Sections │      Live Canvas (iframe)          │ Style/Content  │
 *   │ (left)   │      resized by device toggle      │ (right)        │
 *   └──────────┴────────────────────────────────────┴────────────────┘
 *
 * Mobile (< md): the three columns collapse into a tabbed accordion so the
 * builder is still usable on phones.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import SuperAdminStoreGate from "@/components/SuperAdminStoreGate";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Undo2, Redo2, Eye, Rocket, Monitor, Tablet, Smartphone, GripVertical,
  Sparkles, LayoutTemplate, Palette, Settings2, X, ChevronRight,
  RotateCcw, Type, ArrowUp, ArrowDown, Layers, ImagePlus, Trash2,
  UploadCloud, Loader2, Plus, Search as SearchIcon, Grid3x3,
} from "lucide-react";
import { DEFAULT_HOMEPAGE_SECTIONS } from "@/contexts/StoreContext";
import { uploadDataUrl } from "@/lib/cloudinary";

/**
 * Sections that render a product grid on the storefront homepage. These
 * sections gain the "Products per row" mobile/desktop controls in the
 * right-hand style panel.
 */
const PRODUCT_GRID_SECTIONS = new Set([
  "best_sellers", "trending", "flash_deals", "recommended",
  "buy_again", "recently_viewed", "all_products",
]);

/**
 * Sections that render a marketing image on the storefront. When the user
 * uploads an image in the right panel, we PATCH `settings.marketing.<slot>`
 * and refresh the iframe so the middle canvas reflects the change instantly.
 */
const SECTION_IMAGE_SLOTS = {
  hero:            { slot: "home_slider",     multi: true,  label: "Hero slider images",   aspect: "banner", hint: "16:6 hero banners rotating on the homepage." },
  middle_banner:   { slot: "middle_banner",   multi: false, label: "Middle banner image",  aspect: "banner", hint: "Wide 16:6 banner shown between product sections." },
  brand_banner:    { slot: "brand_banner",    multi: false, label: "Brand banner image",   aspect: "banner", hint: "Featured brand showcase." },
  festival_banner: { slot: "festival_banner", multi: true,  label: "Festival banners",     aspect: "banner", hint: "Seasonal creatives." },
  offer_banner:    { slot: "offer_banner",    multi: true,  label: "Offer banners",        aspect: "banner", hint: "Active deals & discount campaigns." },
  app_banner:      { slot: "app_banner",      multi: false, label: "App download banner",  aspect: "square", hint: "Square creative used across app promo blocks." },
  bottom_banner:   { slot: "bottom_banner",   multi: false, label: "Bottom banner image",  aspect: "banner", hint: "Footer-adjacent banner above the offer strip." },
};
const emptyBanner = () => ({ id: `b-${Math.random().toString(36).slice(2, 8)}${Date.now().toString(36)}`, image: "", title: "", subtitle: "", link_to: "", is_active: true });

// Device breakpoints for the middle canvas.
const DEVICES = {
  desktop: { label: "Desktop", icon: Monitor, w: "100%",  frame: "w-full" },
  tablet:  { label: "Tablet",  icon: Tablet,  w: "820px", frame: "" },
  mobile:  { label: "Mobile",  icon: Smartphone, w: "390px", frame: "" },
};

// Padding presets exposed in the right panel for each section.
const PADDING_PRESETS = [
  { key: "sm", label: "Sm" },
  { key: "md", label: "Md" },
  { key: "lg", label: "Lg" },
];

export default function WebsiteBuilder() {
  return (
    <SuperAdminStoreGate
      pageTitle="Website Builder"
      pageSubtitle="Compose your storefront homepage visually. Publish when ready."
      autoPickPrimary
    >
      <WebsiteBuilderInner />
    </SuperAdminStoreGate>
  );
}

function normalise(sections) {
  // Guarantee every section carries the extended fields we render in the
  // right panel, without ever losing anything the backend has stored.
  return sections.map((s) => ({
    key: s.key,
    label: s.label ?? DEFAULT_HOMEPAGE_SECTIONS.find((d) => d.key === s.key)?.label ?? s.key,
    enabled: s.enabled !== false,
    title_override: s.title_override ?? "",
    subtitle: s.subtitle ?? "",
    bg: s.bg ?? "",
    padding: s.padding ?? "md",
    per_row_mobile: Number(s.per_row_mobile) || 0,
    per_row_desktop: Number(s.per_row_desktop) || 0,
    // Sprint 79 — total-limit + content-filter controls
    limit: Number(s.limit) || 0,
    filter_categories: Array.isArray(s.filter_categories) ? s.filter_categories : [],
    filter_brands: Array.isArray(s.filter_brands) ? s.filter_brands : [],
  }));
}

const DEFAULT_SEO = { title: "", description: "", keywords: "" };
function normaliseSeo(seo) {
  return {
    title: (seo?.title ?? "").toString(),
    description: (seo?.description ?? "").toString(),
    keywords: (seo?.keywords ?? "").toString(),
  };
}

// Sprint 79 — Global Products-Per-Row (applies to every product grid section).
const DEFAULT_GRID = { per_row_mobile: 0, per_row_desktop: 0 };
function normaliseGrid(g) {
  return {
    per_row_mobile: Number(g?.per_row_mobile) || 0,
    per_row_desktop: Number(g?.per_row_desktop) || 0,
  };
}

function WebsiteBuilderInner() {
  const [sections, setSections] = useState(() => normalise(DEFAULT_HOMEPAGE_SECTIONS));
  const [seo, setSeo] = useState(DEFAULT_SEO);
  const [grid, setGrid] = useState(DEFAULT_GRID);
  const [commerceAll, setCommerceAll] = useState({});
  const [marketing, setMarketing] = useState({}); // { home_slider: [...], middle_banner: [...], ... }
  const [marketingSaving, setMarketingSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [device, setDevice] = useState("desktop");
  const [selectedKey, setSelectedKey] = useState(null);
  // Sprint 79 — Catalog data for the per-section Content Filter picker.
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  // Undo/redo history — array of section snapshots. `hIdx` points at "current".
  const [history, setHistory] = useState([normalise(DEFAULT_HOMEPAGE_SECTIONS)]);
  const [hIdx, setHIdx] = useState(0);
  // Dirty flag = current snapshot differs from the last-saved one.
  const savedRef = useRef(JSON.stringify({ sections: normalise(DEFAULT_HOMEPAGE_SECTIONS), seo: DEFAULT_SEO, grid: DEFAULT_GRID }));
  const dirty = JSON.stringify({ sections, seo, grid }) !== savedRef.current;
  // Mobile pane toggle: 'sections' | 'canvas' | 'style'
  const [mobilePane, setMobilePane] = useState("canvas");

  const iframeRef = useRef(null);

  const pushHistory = (next) => {
    setHistory((prev) => {
      const trimmed = prev.slice(0, hIdx + 1);
      trimmed.push(next);
      // Cap at 40 snapshots to keep memory tiny.
      return trimmed.length > 40 ? trimmed.slice(trimmed.length - 40) : trimmed;
    });
    setHIdx((i) => (i + 1 > 39 ? 39 : i + 1));
  };
  const commit = (updater) => {
    setSections((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      pushHistory(next);
      return next;
    });
  };
  const undo = () => {
    if (hIdx === 0) return;
    const target = history[hIdx - 1];
    setHIdx(hIdx - 1);
    setSections(target);
  };
  const redo = () => {
    if (hIdx >= history.length - 1) return;
    const target = history[hIdx + 1];
    setHIdx(hIdx + 1);
    setSections(target);
  };

  const marketingRef = useRef({});
  // Initial load — sections + SEO come from UI Customization (the SAME source
  // the customer site reads), with a fallback to legacy commerce.homepage
  // for older stores. Banners still live under settings.marketing (per store).
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [storeRes, uiRes, catRes, brandRes] = await Promise.all([
          api.get("/store/settings").catch(() => ({ data: {} })),
          api.get("/ui-customization").catch(() => ({ data: {} })),
          api.get("/categories?limit=200").catch(() => ({ data: { items: [] } })),
          api.get("/brands?limit=200").catch(() => ({ data: { items: [] } })),
        ]);
        const data = storeRes.data || {};
        const ui = uiRes.data || {};
        const commerce = data?.settings?.commerce || {};
        const mkt = data?.settings?.marketing || {};
        if (!alive) return;
        setCommerceAll(commerce);
        setCategories((catRes.data?.items || []).filter((c) => c.is_active !== false));
        setBrands((brandRes.data?.items || []).filter((b) => b.is_active !== false));
        // Load marketing banner slots relevant to the builder.
        const mktMap = {};
        Object.values(SECTION_IMAGE_SLOTS).forEach(({ slot }) => {
          mktMap[slot] = Array.isArray(mkt[slot]) ? mkt[slot] : [];
        });
        // Preserve any other marketing slots so we don't wipe them on save.
        Object.entries(mkt).forEach(([k, v]) => {
          if (!(k in mktMap)) mktMap[k] = v;
        });
        setMarketing(mktMap);
        marketingRef.current = mktMap;
        // Prefer ui.homepage.sections / ui.homepage.seo (this is what the
        // customer site consumes). Fall back to commerce.homepage for older
        // stores that were configured before UI Customization existed.
        const uiHome = ui?.homepage || {};
        const saved = (Array.isArray(uiHome.sections) && uiHome.sections.length)
          ? uiHome.sections
          : commerce.homepage?.sections;
        const savedSeo = normaliseSeo(uiHome.seo || commerce.homepage?.seo);
        const savedGrid = normaliseGrid(uiHome.grid || commerce.homepage?.grid);
        setSeo(savedSeo);
        setGrid(savedGrid);
        let next;
        if (Array.isArray(saved) && saved.length) {
          const byKey = Object.fromEntries(saved.map((s) => [s.key, s]));
          const merged = [];
          saved.forEach((s) => {
            const def = DEFAULT_HOMEPAGE_SECTIONS.find((d) => d.key === s.key);
            if (s?.key && byKey[s.key] && def) merged.push({ ...def, ...s });
          });
          DEFAULT_HOMEPAGE_SECTIONS.forEach((d) => { if (!byKey[d.key]) merged.push(d); });
          next = normalise(merged);
        } else {
          next = normalise(DEFAULT_HOMEPAGE_SECTIONS);
        }
        setSections(next);
        setHistory([next]);
        setHIdx(0);
        savedRef.current = JSON.stringify({ sections: next, seo: savedSeo, grid: savedGrid });
      } catch (e) {
        toast.error(errMsg(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const selected = useMemo(
    () => sections.find((s) => s.key === selectedKey) || null,
    [sections, selectedKey]
  );
  const enabledCount = useMemo(() => sections.filter((s) => s.enabled).length, [sections]);

  const toggleSection = (key) => commit((prev) =>
    prev.map((s) => (s.key === key ? { ...s, enabled: !s.enabled } : s))
  );
  const moveSection = (key, delta) => commit((prev) => {
    const idx = prev.findIndex((s) => s.key === key);
    const target = idx + delta;
    if (idx < 0 || target < 0 || target >= prev.length) return prev;
    const next = [...prev];
    [next[idx], next[target]] = [next[target], next[idx]];
    return next;
  });
  const updateSelected = (patch) => commit((prev) =>
    prev.map((s) => (s.key === selectedKey ? { ...s, ...patch } : s))
  );
  const resetLayout = () => {
    if (!confirm("Reset all sections to defaults? Unsaved changes will be lost.")) return;
    const fresh = normalise(DEFAULT_HOMEPAGE_SECTIONS);
    setSections(fresh);
    setHistory((prev) => [...prev, fresh]);
    setHIdx((i) => i + 1);
  };

  // Native HTML5 drag-and-drop reorder — no extra dep.
  const [dragKey, setDragKey] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const onDragStart = (k) => (e) => { setDragKey(k); try { e.dataTransfer.effectAllowed = "move"; } catch {/*noop*/} };
  const onDragOver  = (k) => (e) => { e.preventDefault(); if (dragOverKey !== k) setDragOverKey(k); };
  const onDragLeave = () => setDragOverKey(null);
  const onDrop      = (k) => (e) => {
    e.preventDefault();
    if (!dragKey || dragKey === k) { setDragKey(null); setDragOverKey(null); return; }
    commit((prev) => {
      const from = prev.findIndex((s) => s.key === dragKey);
      const to = prev.findIndex((s) => s.key === k);
      if (from < 0 || to < 0) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setDragKey(null); setDragOverKey(null);
  };
  const onDragEnd = () => { setDragKey(null); setDragOverKey(null); };

  const publish = async () => {
    setSaving(true);
    try {
      // Write to UI Customization — the source of truth the customer site
      // reads. Also mirror to commerce.homepage for backward compatibility
      // with any legacy consumers.
      await api.patch("/ui-customization", {
        homepage: { sections, seo, grid },
      });
      await api.patch("/store/settings/commerce", {
        ...commerceAll,
        homepage: { ...(commerceAll.homepage || {}), sections, seo, grid },
      }).catch(() => { /* best-effort mirror */ });
      savedRef.current = JSON.stringify({ sections, seo, grid });
      toast.success("Website published");
      // Refresh the live canvas so the storefront reflects the new layout.
      if (iframeRef.current) {
        try { iframeRef.current.src = iframeRef.current.src; } catch {/*noop*/}
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  // Persist marketing changes for a single slot. Uses a ref for the freshest
  // marketing snapshot so back-to-back saves across different slots never
  // clobber each other (setState is async — this fixes the race). The
  // backend now shallow-merges per slot so only the changed slot is written.
  const saveMarketingSlot = async (slot, nextList) => {
    const nextMarketing = { ...(marketingRef.current || {}), [slot]: nextList };
    marketingRef.current = nextMarketing;
    setMarketing(nextMarketing);
    setMarketingSaving(true);
    try {
      // Send only the changed slot — backend shallow-merges into existing.
      await api.patch("/store/settings/marketing", { [slot]: nextList });
      if (iframeRef.current) {
        try { iframeRef.current.src = iframeRef.current.src; } catch {/*noop*/}
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setMarketingSaving(false);
    }
  };

  const canUndo = hIdx > 0;
  const canRedo = hIdx < history.length - 1;

  return (
    <div className="min-h-[calc(100vh-56px)] md:min-h-[calc(100vh-64px)] bg-white" data-testid="website-builder">
      <ActionBar
        canUndo={canUndo}
        canRedo={canRedo}
        onUndo={undo}
        onRedo={redo}
        device={device}
        setDevice={setDevice}
        onPreview={() => window.open("/", "_blank", "noopener,noreferrer")}
        onPublish={publish}
        saving={saving}
        dirty={dirty}
        onReset={resetLayout}
        enabledCount={enabledCount}
        totalCount={sections.length}
      />

      {/* Mobile pane toggle */}
      <div className="md:hidden sticky top-[104px] z-20 bg-white border-b border-slate-100 px-3 py-2 flex gap-1.5 overflow-x-auto no-scrollbar">
        {[
          { k: "sections", label: "Sections", icon: Layers },
          { k: "canvas",   label: "Canvas",   icon: LayoutTemplate },
          { k: "style",    label: "Style",    icon: Palette },
        ].map((p) => (
          <button
            key={p.k}
            type="button"
            onClick={() => setMobilePane(p.k)}
            className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-xs font-semibold transition-all ${
              mobilePane === p.k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
            data-testid={`wb-mobile-tab-${p.k}`}
          >
            <p.icon className="w-3.5 h-3.5" /> {p.label}
          </button>
        ))}
      </div>

      <div className="flex flex-col md:grid md:grid-cols-[300px_minmax(0,1fr)_340px] gap-0">
        {/* LEFT — Sections list */}
        <aside
          className={`border-r border-slate-100 bg-white ${mobilePane === "sections" ? "" : "hidden"} md:block`}
          data-testid="wb-left-pane"
        >
          {/* Homepage-level SEO shortcut */}
          <div className="px-3 pt-5 pb-2 space-y-2">
            <button
              type="button"
              onClick={() => { setSelectedKey("__seo"); setMobilePane("style"); }}
              className={`w-full flex items-center gap-2.5 px-3 h-11 rounded-xl transition-all border ${
                selectedKey === "__seo"
                  ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-400 hover:bg-slate-50"
              }`}
              data-testid="wb-open-seo"
            >
              <SearchIcon className={`w-3.5 h-3.5 ${selectedKey === "__seo" ? "text-white/80" : "text-slate-500"}`} />
              <div className="min-w-0 flex-1 text-left">
                <div className="text-[12.5px] font-semibold leading-tight">Homepage SEO</div>
                <div className={`text-[10px] uppercase tracking-wider ${selectedKey === "__seo" ? "text-white/60" : "text-slate-400"}`}>
                  Meta title · description · keywords
                </div>
              </div>
              <ChevronRight className={`w-3.5 h-3.5 ${selectedKey === "__seo" ? "text-white/70" : "text-slate-400"}`} />
            </button>
            {/* Sprint 79 — Global Products-Per-Row shortcut */}
            <button
              type="button"
              onClick={() => { setSelectedKey("__grid"); setMobilePane("style"); }}
              className={`w-full flex items-center gap-2.5 px-3 h-11 rounded-xl transition-all border ${
                selectedKey === "__grid"
                  ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-400 hover:bg-slate-50"
              }`}
              data-testid="wb-open-grid"
            >
              <Grid3x3 className={`w-3.5 h-3.5 ${selectedKey === "__grid" ? "text-white/80" : "text-slate-500"}`} />
              <div className="min-w-0 flex-1 text-left">
                <div className="text-[12.5px] font-semibold leading-tight">Global Products / Row</div>
                <div className={`text-[10px] uppercase tracking-wider ${selectedKey === "__grid" ? "text-white/60" : "text-slate-400"}`}>
                  Applies to every product section
                </div>
              </div>
              <span className={`text-[10.5px] font-mono ${selectedKey === "__grid" ? "text-white/70" : "text-slate-400"}`}>
                {grid.per_row_mobile || "·"}/{grid.per_row_desktop || "·"}
              </span>
            </button>
          </div>
          <div className="px-5 pt-3 pb-3 flex items-center gap-2">
            <Layers className="w-4 h-4 text-slate-500" />
            <div className="text-[13px] font-semibold text-slate-900">Sections</div>
            <span className="ml-auto text-[10.5px] font-mono text-slate-500">
              {enabledCount}/{sections.length}
            </span>
          </div>
          <div className="px-2 pb-6 space-y-1" data-testid="wb-sections-list">
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-11 mx-2 rounded-xl bg-slate-100 animate-pulse" />
              ))
            ) : (
              sections.map((s, i) => {
                const isSelected = selectedKey === s.key;
                const isDragOver = dragOverKey === s.key && dragKey !== s.key;
                return (
                  <div
                    key={s.key}
                    draggable
                    onDragStart={onDragStart(s.key)}
                    onDragOver={onDragOver(s.key)}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop(s.key)}
                    onDragEnd={onDragEnd}
                    onClick={() => { setSelectedKey(s.key); setMobilePane("style"); }}
                    className={`group flex items-center gap-2 px-2.5 py-2 rounded-xl cursor-pointer transition-all
                      ${isSelected ? "bg-slate-900 text-white shadow-sm" : "hover:bg-slate-50 text-slate-700"}
                      ${isDragOver ? "ring-2 ring-slate-900" : ""}
                      ${dragKey === s.key ? "opacity-40" : ""}
                    `}
                    data-testid={`wb-section-${s.key}`}
                  >
                    <GripVertical className={`w-3.5 h-3.5 shrink-0 ${isSelected ? "text-white/60" : "text-slate-400"} cursor-grab active:cursor-grabbing`} />
                    <span className={`w-6 shrink-0 text-[10px] font-mono ${isSelected ? "text-white/60" : "text-slate-400"}`}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className={`text-[12.5px] font-semibold truncate ${!s.enabled && !isSelected ? "line-through opacity-60" : ""}`}>
                        {s.label}
                      </div>
                      <div className={`text-[10px] uppercase tracking-wider truncate ${isSelected ? "text-white/50" : "text-slate-400"}`}>
                        {s.key}
                      </div>
                    </div>
                    <Switch
                      checked={s.enabled}
                      onCheckedChange={() => toggleSection(s.key)}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`wb-toggle-${s.key}`}
                    />
                  </div>
                );
              })
            )}
          </div>
        </aside>

        {/* MIDDLE — Live canvas */}
        <section
          className={`bg-slate-50 min-h-[520px] md:min-h-[calc(100vh-128px)] flex flex-col items-center ${mobilePane === "canvas" ? "" : "hidden"} md:flex`}
          data-testid="wb-canvas-pane"
        >
          <div className="py-4 md:py-6 px-3 w-full flex items-center justify-center gap-2">
            <div className="hidden md:flex items-center gap-1.5 text-[11px] text-slate-500">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Live preview
            </div>
            <div className="ml-auto md:ml-0 md:mx-auto text-[11px] text-slate-500 font-mono">
              {DEVICES[device].label} · {DEVICES[device].w}
            </div>
          </div>
          <div className="w-full flex-1 px-3 md:px-6 pb-6 overflow-auto flex justify-center">
            <div
              className={`bg-white rounded-2xl shadow-[0_20px_60px_-24px_rgba(15,23,42,0.25)] border border-slate-200 overflow-hidden transition-all duration-300`}
              style={{
                width: device === "desktop" ? "100%" : DEVICES[device].w,
                maxWidth: device === "desktop" ? "1180px" : DEVICES[device].w,
                height: device === "mobile" ? "780px" : (device === "tablet" ? "820px" : "72vh"),
                minHeight: "560px",
              }}
              data-testid={`wb-canvas-frame-${device}`}
            >
              <iframe
                ref={iframeRef}
                src="/"
                title="Storefront preview"
                className="w-full h-full border-0 bg-white"
                data-testid="wb-canvas-iframe"
              />
            </div>
          </div>
        </section>

        {/* RIGHT — Style/content controls */}
        <aside
          className={`border-l border-slate-100 bg-white ${mobilePane === "style" ? "" : "hidden"} md:block`}
          data-testid="wb-right-pane"
        >
          {selectedKey === "__seo" ? (
            <SeoPanel
              seo={seo}
              onChange={(patch) => setSeo((prev) => ({ ...prev, ...patch }))}
              onClose={() => setSelectedKey(null)}
            />
          ) : selectedKey === "__grid" ? (
            <GridPanel
              grid={grid}
              onChange={(patch) => setGrid((prev) => ({ ...prev, ...patch }))}
              onClose={() => setSelectedKey(null)}
            />
          ) : selected ? (
            <StylePanel
              section={selected}
              onChange={updateSelected}
              onMoveUp={() => moveSection(selected.key, -1)}
              onMoveDown={() => moveSection(selected.key, 1)}
              onClose={() => setSelectedKey(null)}
              slotConfig={SECTION_IMAGE_SLOTS[selected.key] || null}
              slotItems={SECTION_IMAGE_SLOTS[selected.key] ? (marketing[SECTION_IMAGE_SLOTS[selected.key].slot] || []) : []}
              onSlotChange={(next) => {
                const cfg = SECTION_IMAGE_SLOTS[selected.key];
                if (cfg) saveMarketingSlot(cfg.slot, next);
              }}
              slotSaving={marketingSaving}
              categories={categories}
              brands={brands}
              globalGrid={grid}
            />
          ) : (
            <EmptyRightPane onOpen={() => sections[0] && setSelectedKey(sections[0].key)} />
          )}
        </aside>
      </div>
    </div>
  );
}

/* ─── Sticky action bar ──────────────────────────────────────────────── */
function ActionBar({
  canUndo, canRedo, onUndo, onRedo, device, setDevice,
  onPreview, onPublish, saving, dirty, onReset, enabledCount, totalCount,
}) {
  return (
    <div
      className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-100 px-3 md:px-6 py-3 flex items-center gap-2 md:gap-3"
      data-testid="wb-action-bar"
    >
      <div className="flex items-center gap-2 min-w-0">
        <div className="w-9 h-9 rounded-xl bg-slate-900 text-white grid place-items-center shrink-0">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="min-w-0 hidden sm:block">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold leading-none">
            Super Admin
          </div>
          <div className="text-[15px] font-semibold text-slate-900 leading-tight truncate">
            Website Builder
          </div>
        </div>
        <span className="hidden lg:inline-flex ml-2 text-[10.5px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-mono">
          {enabledCount}/{totalCount} live
        </span>
      </div>

      {/* Undo / Redo */}
      <div className="flex items-center gap-1 ml-1 md:ml-3">
        <IconBtn onClick={onUndo} disabled={!canUndo} testId="wb-undo" title="Undo">
          <Undo2 className="w-4 h-4" />
        </IconBtn>
        <IconBtn onClick={onRedo} disabled={!canRedo} testId="wb-redo" title="Redo">
          <Redo2 className="w-4 h-4" />
        </IconBtn>
        <IconBtn onClick={onReset} testId="wb-reset" title="Reset to defaults">
          <RotateCcw className="w-4 h-4" />
        </IconBtn>
      </div>

      {/* Device switcher */}
      <div
        className="mx-auto flex items-center gap-1 bg-slate-100 rounded-xl p-1"
        data-testid="wb-device-switcher"
      >
        {Object.entries(DEVICES).map(([k, cfg]) => {
          const active = device === k;
          const Icon = cfg.icon;
          return (
            <button
              key={k}
              type="button"
              onClick={() => setDevice(k)}
              className={`h-8 px-2.5 md:px-3 rounded-lg flex items-center gap-1.5 text-[12px] font-semibold transition-all ${
                active
                  ? "bg-white text-slate-900 shadow-[0_1px_2px_rgba(15,23,42,0.06),0_4px_12px_-6px_rgba(15,23,42,0.12)]"
                  : "text-slate-500 hover:text-slate-800"
              }`}
              data-testid={`wb-device-${k}`}
              title={cfg.label}
            >
              <Icon className="w-3.5 h-3.5" />
              <span className="hidden md:inline">{cfg.label}</span>
            </button>
          );
        })}
      </div>

      {/* Preview + Publish */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onPreview}
          className="sa-btn-ghost h-9 px-3 inline-flex items-center gap-1.5 text-[13px] font-semibold"
          data-testid="wb-preview-btn"
        >
          <Eye className="w-4 h-4" /> <span className="hidden md:inline">Preview</span>
        </button>
        <button
          type="button"
          onClick={onPublish}
          disabled={saving}
          className="sa-btn-primary h-9 px-3 md:px-4 inline-flex items-center gap-1.5 text-[13px] disabled:opacity-60 disabled:cursor-not-allowed relative"
          data-testid="wb-publish-btn"
        >
          <Rocket className="w-4 h-4" />
          <span>{saving ? "Publishing…" : "Save & Publish"}</span>
          {dirty && !saving && (
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-amber-400 ring-2 ring-white" aria-hidden />
          )}
        </button>
      </div>
    </div>
  );
}

function IconBtn({ children, onClick, disabled, title, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="sa-btn-ghost w-9 h-9 grid place-items-center disabled:opacity-40 disabled:cursor-not-allowed"
      data-testid={testId}
    >
      {children}
    </button>
  );
}

/* ─── Right panel — style/content controls for the selected section ──── */
function StylePanel({ section, onChange, onMoveUp, onMoveDown, onClose, slotConfig, slotItems, onSlotChange, slotSaving, categories = [], brands = [], globalGrid = { per_row_mobile: 0, per_row_desktop: 0 } }) {
  return (
    <div className="p-5 space-y-5" data-testid={`wb-style-panel-${section.key}`}>
      <div className="flex items-start gap-2">
        <div className="w-9 h-9 rounded-xl bg-slate-900 text-white grid place-items-center shrink-0">
          <Settings2 className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Section</div>
          <div className="text-[15px] font-semibold text-slate-900 truncate">{section.label}</div>
          <div className="text-[10.5px] font-mono text-slate-400 truncate">{section.key}</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="sa-btn-ghost w-8 h-8 grid place-items-center"
          data-testid="wb-close-style"
          title="Close"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Image / banner uploader — only for sections mapped to a marketing slot */}
      {slotConfig && (
        <BannerUploader
          config={slotConfig}
          items={slotItems}
          onChange={onSlotChange}
          saving={slotSaving}
        />
      )}

      {/* Visibility */}
      <FieldRow label="Visibility" hint="Hide from the live homepage without deleting.">
        <div className="flex items-center gap-3">
          <Switch
            checked={section.enabled}
            onCheckedChange={(v) => onChange({ enabled: v })}
            data-testid="wb-style-enabled"
          />
          <span className={`text-[12.5px] font-semibold ${section.enabled ? "text-emerald-600" : "text-slate-400"}`}>
            {section.enabled ? "Live" : "Hidden"}
          </span>
        </div>
      </FieldRow>

      {/* Order */}
      <FieldRow label="Order" hint="Move up or down in the flow.">
        <div className="flex gap-2">
          <button type="button" onClick={onMoveUp} className="sa-btn-ghost h-9 px-3 inline-flex items-center gap-1.5 text-[12.5px]" data-testid="wb-style-up">
            <ArrowUp className="w-3.5 h-3.5" /> Up
          </button>
          <button type="button" onClick={onMoveDown} className="sa-btn-ghost h-9 px-3 inline-flex items-center gap-1.5 text-[12.5px]" data-testid="wb-style-down">
            <ArrowDown className="w-3.5 h-3.5" /> Down
          </button>
        </div>
      </FieldRow>

      {/* Content overrides */}
      <FieldRow label="Heading override" hint="Override the section title (optional).">
        <div className="relative">
          <Type className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={section.title_override}
            onChange={(e) => onChange({ title_override: e.target.value })}
            placeholder={section.label}
            className="pl-9 h-10 rounded-xl border-slate-200 bg-white"
            data-testid="wb-style-title"
          />
        </div>
      </FieldRow>

      <FieldRow label="Subtitle" hint="Short one-liner shown below the title.">
        <Input
          value={section.subtitle}
          onChange={(e) => onChange({ subtitle: e.target.value })}
          placeholder="Optional subtitle"
          className="h-10 rounded-xl border-slate-200 bg-white"
          data-testid="wb-style-subtitle"
        />
      </FieldRow>

      {/* Style */}
      <FieldRow label="Background" hint="Hex color, e.g. #FFF4E5.">
        <div className="flex items-center gap-2">
          <div
            className="w-10 h-10 rounded-xl border border-slate-200"
            style={{ background: section.bg || "#ffffff" }}
            aria-hidden
          />
          <Input
            value={section.bg}
            onChange={(e) => onChange({ bg: e.target.value })}
            placeholder="#FFFFFF"
            className="h-10 rounded-xl border-slate-200 bg-white flex-1 font-mono text-[12.5px]"
            data-testid="wb-style-bg"
          />
          {section.bg && (
            <button
              type="button"
              onClick={() => onChange({ bg: "" })}
              className="text-slate-400 hover:text-slate-700 text-[11px] font-semibold"
              data-testid="wb-style-bg-clear"
            >
              Clear
            </button>
          )}
        </div>
      </FieldRow>

      <FieldRow label="Spacing" hint="Vertical padding around this section.">
        <div className="flex gap-1.5">
          {PADDING_PRESETS.map((p) => {
            const active = section.padding === p.key;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => onChange({ padding: p.key })}
                className={`flex-1 h-10 rounded-xl text-[12.5px] font-semibold transition-all border ${
                  active
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}
                data-testid={`wb-style-pad-${p.key}`}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </FieldRow>

      {/* Sprint 79 — Per-section grid density controls REMOVED. Grid density
          is now a GLOBAL homepage setting (Website Builder → Global
          Products / Row). This keeps admin controls consistent with
          "one dial changes every product section" behaviour. */}

      {/* Sprint 79 — Total product limit + Content filter (categories/brands) */}
      {PRODUCT_GRID_SECTIONS.has(section.key) && (
        <>
          <FieldRow
            label="Total product limit"
            hint="Cap the number of products shown in this section. Choose 'All' to show every match."
          >
            <div className="grid grid-cols-5 gap-1.5" data-testid="wb-style-limit-group">
              {[0, 6, 10, 20, 40].map((n) => {
                const active = (section.limit || 0) === n;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => onChange({ limit: n })}
                    className={`h-9 rounded-lg text-[12px] font-semibold transition-all border ${
                      active
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                    }`}
                    data-testid={`wb-style-limit-${n}`}
                    aria-pressed={active}
                  >
                    {n === 0 ? "All" : n}
                  </button>
                );
              })}
            </div>
          </FieldRow>

          <FieldRow
            label="Content filter · Categories"
            hint="Show only products from the selected categories. Leave empty to include all."
          >
            <ChipMultiSelect
              items={categories}
              value={section.filter_categories || []}
              onChange={(next) => onChange({ filter_categories: next })}
              testId="wb-style-filter-categories"
              placeholder="Type to search categories…"
            />
          </FieldRow>

          <FieldRow
            label="Content filter · Brands"
            hint="Show only products from the selected brands. Leave empty to include all."
          >
            <ChipMultiSelect
              items={brands}
              value={section.filter_brands || []}
              onChange={(next) => onChange({ filter_brands: next })}
              testId="wb-style-filter-brands"
              placeholder="Type to search brands…"
            />
          </FieldRow>
        </>
      )}

      <div className="pt-2 text-[11px] text-slate-400 leading-relaxed border-t border-slate-100">
        Changes are staged locally. Hit <span className="font-semibold text-slate-700">Save &amp; Publish</span> in the action bar to go live.
      </div>
    </div>
  );
}

function FieldRow({ label, hint, children }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      </div>
      {children}
      {hint && <div className="mt-1.5 text-[10.5px] text-slate-400 leading-snug">{hint}</div>}
    </div>
  );
}

function EmptyRightPane({ onOpen }) {
  return (
    <div className="h-full p-6 flex flex-col items-center justify-center text-center gap-3" data-testid="wb-right-empty">
      <div className="w-12 h-12 rounded-2xl bg-slate-100 text-slate-500 grid place-items-center">
        <Palette className="w-5 h-5" />
      </div>
      <div className="text-[14px] font-semibold text-slate-900">Pick a section</div>
      <p className="text-[12px] text-slate-500 leading-relaxed max-w-[220px]">
        Choose any section from the left rail to edit its heading, background, spacing and visibility.
      </p>
      <button
        type="button"
        onClick={onOpen}
        className="sa-btn-ghost h-9 px-3 inline-flex items-center gap-1.5 text-[12.5px] mt-1"
        data-testid="wb-right-empty-open"
      >
        Open first section <ChevronRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

/* ─── Banner / image uploader inside the right panel ──────────────────── */
const MAX_MB = 5;
const ACCEPT = "image/png,image/jpeg,image/webp,image/svg+xml";

function BannerUploader({ config, items, onChange, saving }) {
  const list = Array.isArray(items) ? items : [];
  const isMulti = !!config.multi;

  const addImage = (dataUrl) => {
    if (!dataUrl) return;
    if (isMulti) {
      onChange([...list, { ...emptyBanner(), image: dataUrl }]);
    } else {
      // Replace-first for single-slot sections; keep any trailing entries.
      const rest = list.slice(1);
      onChange([{ ...(list[0] || emptyBanner()), image: dataUrl }, ...rest]);
    }
    toast.success("Image uploaded · preview updating");
  };
  const updateItem = (id, patch) => onChange(list.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  const removeItem = (id) => onChange(list.filter((it) => it.id !== id));

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 space-y-3" data-testid="wb-banner-uploader">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
            <ImagePlus className="w-3.5 h-3.5" /> {config.label}
            {saving && <Loader2 className="w-3 h-3 animate-spin text-slate-400 ml-1" />}
          </div>
          <div className="text-[10.5px] text-slate-400 mt-0.5 leading-snug">{config.hint}</div>
        </div>
        <span className="text-[10px] font-mono text-slate-400 shrink-0 mt-0.5">{list.length}{isMulti ? "" : "/1"}</span>
      </div>

      {/* Existing images — Title/Link inputs live OUTSIDE the preview box */}
      {list.length > 0 && (
        <div className="grid grid-cols-1 gap-3">
          {list.map((it) => (
            <div key={it.id} className="rounded-xl bg-white border border-slate-200 overflow-hidden" data-testid={`wb-banner-item-${it.id}`}>
              {/* Preview only — no overlaid inputs */}
              <div className={`relative w-full ${config.aspect === "square" ? "aspect-square" : "aspect-[16/6]"} bg-slate-100 group`}>
                {it.image ? (
                  <img src={it.image} alt="" className="w-full h-full object-cover" data-testid={`wb-banner-preview-${it.id}`} />
                ) : (
                  <div className="w-full h-full grid place-items-center text-slate-400">
                    <ImagePlus className="w-6 h-6" />
                  </div>
                )}
                <div className="absolute top-2 right-2 flex gap-1.5 opacity-100">
                  <FilePicker
                    onPick={(url) => updateItem(it.id, { image: url })}
                    className="h-7 px-2.5 rounded-lg bg-white/95 text-slate-900 text-[11px] font-semibold shadow-sm inline-flex items-center gap-1 hover:bg-white"
                    testId={`wb-banner-replace-${it.id}`}
                    folder={`babu-baazar/banners/${config.slot}`}
                  >
                    <UploadCloud className="w-3 h-3" /> Replace
                  </FilePicker>
                  <button
                    type="button"
                    onClick={() => removeItem(it.id)}
                    className="h-7 px-2.5 rounded-lg bg-white/95 text-red-600 text-[11px] font-semibold shadow-sm inline-flex items-center gap-1 hover:bg-white"
                    data-testid={`wb-banner-remove-${it.id}`}
                  >
                    <Trash2 className="w-3 h-3" /> Remove
                  </button>
                </div>
              </div>
              {/* Fields — moved BELOW preview per builder UI update */}
              <div className="p-3 space-y-2 border-t border-slate-100">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Title</div>
                  <input
                    value={it.title || ""}
                    onChange={(e) => updateItem(it.id, { title: e.target.value })}
                    placeholder="Optional headline shown over the banner"
                    className="w-full h-9 rounded-lg border border-slate-200 bg-white text-[12.5px] px-2.5 focus:outline-none focus:border-slate-900"
                    data-testid={`wb-banner-title-${it.id}`}
                  />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Link URL</div>
                  <input
                    value={it.link_to || ""}
                    onChange={(e) => updateItem(it.id, { link_to: e.target.value })}
                    placeholder="/shop  or  https://…"
                    className="w-full h-9 rounded-lg border border-slate-200 bg-white text-[12px] px-2.5 font-mono focus:outline-none focus:border-slate-900"
                    data-testid={`wb-banner-link-${it.id}`}
                  />
                </div>
                <label className="flex items-center gap-2 text-[11.5px] text-slate-600 pt-1">
                  <input
                    type="checkbox"
                    checked={it.is_active !== false}
                    onChange={(e) => updateItem(it.id, { is_active: e.target.checked })}
                    className="rounded border-slate-300"
                    data-testid={`wb-banner-active-${it.id}`}
                  />
                  Show on storefront
                </label>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload zone */}
      {(isMulti || list.length === 0) && (
        <FilePicker
          onPick={addImage}
          className={`w-full ${config.aspect === "square" ? "aspect-square" : "aspect-[16/6]"} rounded-xl border-2 border-dashed border-slate-300 bg-white hover:border-slate-900 hover:bg-slate-50 transition-all grid place-items-center text-slate-500`}
          testId="wb-banner-upload-zone"
          folder={`babu-baazar/banners/${config.slot}`}
        >
          <div className="text-center px-4">
            <div className="w-10 h-10 mx-auto rounded-2xl bg-slate-900 text-white grid place-items-center mb-2">
              <UploadCloud className="w-4 h-4" />
            </div>
            <div className="text-[12.5px] font-semibold text-slate-900">Upload image</div>
            <div className="text-[10.5px] text-slate-400 mt-0.5">PNG · JPG · WebP · max {MAX_MB} MB</div>
          </div>
        </FilePicker>
      )}
      {!isMulti && list.length > 0 && (
        <div className="text-[10.5px] text-slate-400 leading-snug">
          Only one image is used for this section. Replace or remove above.
        </div>
      )}
      {isMulti && (
        <button
          type="button"
          onClick={() => onChange([...list, emptyBanner()])}
          className="w-full h-9 rounded-xl border border-dashed border-slate-300 text-slate-500 text-[12px] font-semibold hover:border-slate-900 hover:text-slate-900 inline-flex items-center justify-center gap-1.5"
          data-testid="wb-banner-add-empty"
        >
          <Plus className="w-3.5 h-3.5" /> Add empty slot
        </button>
      )}
    </div>
  );
}

/** Local file picker that uploads to Cloudinary and calls onPick(url).
 *
 * Fixes the "uploaded banner doesn't appear on live site" bug: large base64
 * dataURLs used to be persisted directly to the marketing settings blob in
 * MongoDB, occasionally silently truncating (doc-size ceiling) or making the
 * public settings response too large to render. We now upload the file to
 * Cloudinary and store the returned CDN URL. On any transient upload failure
 * we fall back to the previous dataURL behaviour so admins are never blocked.
 */
function FilePicker({ children, onPick, className, testId, folder }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    const mb = file.size / (1024 * 1024);
    if (mb > MAX_MB) { toast.error(`Image is too large (${mb.toFixed(1)} MB) — max ${MAX_MB} MB`); return; }
    setBusy(true);
    // 1) Read as dataURL locally so we can preview immediately and use it as
    //    a fallback if the backend upload fails.
    const dataUrl = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => resolve("");
      reader.readAsDataURL(file);
    });
    if (!dataUrl) {
      toast.error("Could not read file");
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    // 2) Upload to Cloudinary. Falls back to the dataURL if Cloudinary is
    //    not configured on this environment or the network hiccups.
    try {
      const res = await uploadDataUrl(dataUrl, {
        folder: folder || "babu-baazar/banners",
        filename: file.name,
      });
      onPick?.(res?.url || dataUrl);
    } catch (err) {
      onPick?.(dataUrl);
      toast.warning("Image saved locally · Cloudinary offline");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  return (
    <button
      type="button"
      onClick={() => inputRef.current?.click()}
      className={className}
      data-testid={testId}
      disabled={busy}
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : children}
      <input ref={inputRef} type="file" accept={ACCEPT} onChange={handle} className="hidden" />
    </button>
  );
}

/* ─── Homepage SEO panel ─────────────────────────────────────────────── */
function SeoPanel({ seo, onChange, onClose }) {
  const kwCount = (seo.keywords || "")
    .split(",").map((s) => s.trim()).filter(Boolean).length;
  return (
    <div className="p-5 space-y-5" data-testid="wb-seo-panel">
      <div className="flex items-start gap-2">
        <div className="w-9 h-9 rounded-xl bg-slate-900 text-white grid place-items-center shrink-0">
          <SearchIcon className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Homepage</div>
          <div className="text-[15px] font-semibold text-slate-900">Search engine settings</div>
          <div className="text-[10.5px] text-slate-400 truncate">Meta title · description · keywords</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="sa-btn-ghost w-8 h-8 grid place-items-center"
          data-testid="wb-close-seo"
          title="Close"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <FieldRow label="Meta title" hint="Shown in browser tabs & Google results. Aim for ≤ 60 chars.">
        <Input
          value={seo.title}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Babu Baazar — Get More, Pay Less"
          maxLength={80}
          className="h-10 rounded-xl border-slate-200 bg-white"
          data-testid="wb-seo-title"
        />
        <div className="mt-1 text-[10.5px] text-slate-400 font-mono">{(seo.title || "").length}/60</div>
      </FieldRow>

      <FieldRow label="Meta description" hint="One-sentence pitch that appears under the title in search results.">
        <textarea
          value={seo.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="Groceries delivered in minutes from your local Babu Store."
          rows={3}
          maxLength={200}
          className="w-full rounded-xl border border-slate-200 bg-white p-2.5 text-[13px] focus:outline-none focus:border-slate-900 resize-none"
          data-testid="wb-seo-description"
        />
        <div className="mt-1 text-[10.5px] text-slate-400 font-mono">{(seo.description || "").length}/160</div>
      </FieldRow>

      <FieldRow label="SEO keywords" hint="Comma-separated. Applied to the homepage <meta name='keywords'> tag.">
        <textarea
          value={seo.keywords}
          onChange={(e) => onChange({ keywords: e.target.value })}
          placeholder="grocery, atta, milk, snacks, personal care, blinkit, quick commerce, babu baazar"
          rows={3}
          className="w-full rounded-xl border border-slate-200 bg-white p-2.5 text-[13px] focus:outline-none focus:border-slate-900 resize-none font-mono"
          data-testid="wb-seo-keywords"
        />
        <div className="mt-1 text-[10.5px] text-slate-400 font-mono">{kwCount} keyword{kwCount === 1 ? "" : "s"}</div>
      </FieldRow>

      <div className="pt-2 text-[11px] text-slate-400 leading-relaxed border-t border-slate-100">
        Hit <span className="font-semibold text-slate-700">Save &amp; Publish</span> to apply. The homepage
        <span className="font-mono text-slate-500"> &lt;head&gt; </span>
        will update on the next visit.
      </div>
    </div>
  );
}


/* ─── Global Products-Per-Row panel (Sprint 79) ──────────────────────── */
function GridPanel({ grid, onChange, onClose }) {
  const OPTIONS = [0, 2, 3, 4, 5, 6, 7, 8]; // 0 = "inherit / not set"
  return (
    <div className="p-5 space-y-5" data-testid="wb-grid-panel">
      <div className="flex items-start gap-2">
        <div className="w-9 h-9 rounded-xl bg-slate-900 text-white grid place-items-center shrink-0">
          <Grid3x3 className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Global</div>
          <div className="text-[15px] font-semibold text-slate-900">Products per row</div>
          <div className="text-[10.5px] text-slate-400 truncate">Applies to every product grid on the homepage</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="sa-btn-ghost w-8 h-8 grid place-items-center"
          data-testid="wb-close-grid"
          title="Close"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <FieldRow
        label={<span className="inline-flex items-center gap-1.5"><Smartphone className="w-3 h-3" /> Mobile</span>}
        hint="Number of cards per row on phones. Choose '—' to fall back to the section default."
      >
        <div className="grid grid-cols-8 gap-1" data-testid="wb-grid-mobile-group">
          {OPTIONS.map((n) => {
            const active = (grid.per_row_mobile || 0) === n;
            return (
              <button
                key={n}
                type="button"
                onClick={() => onChange({ per_row_mobile: n })}
                className={`h-10 rounded-lg text-[13px] font-semibold transition-all border ${
                  active
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}
                data-testid={`wb-grid-mobile-${n}`}
                aria-pressed={active}
              >
                {n === 0 ? "—" : n}
              </button>
            );
          })}
        </div>
      </FieldRow>

      <FieldRow
        label={<span className="inline-flex items-center gap-1.5"><Monitor className="w-3 h-3" /> Desktop</span>}
        hint="Number of cards per row on laptops / desktops."
      >
        <div className="grid grid-cols-8 gap-1" data-testid="wb-grid-desktop-group">
          {OPTIONS.map((n) => {
            const active = (grid.per_row_desktop || 0) === n;
            return (
              <button
                key={n}
                type="button"
                onClick={() => onChange({ per_row_desktop: n })}
                className={`h-10 rounded-lg text-[13px] font-semibold transition-all border ${
                  active
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}
                data-testid={`wb-grid-desktop-${n}`}
                aria-pressed={active}
              >
                {n === 0 ? "—" : n}
              </button>
            );
          })}
        </div>
      </FieldRow>

      <div className="pt-2 text-[11px] text-slate-400 leading-relaxed border-t border-slate-100">
        Per-section overrides live inside each section's Style panel. When a
        section has its own value, that wins over the global setting.
      </div>
    </div>
  );
}

/* ─── Multi-select chip picker for categories / brands (Sprint 79) ───── */
function ChipMultiSelect({ items, value, onChange, testId, placeholder }) {
  const [q, setQ] = useState("");
  const selected = new Set(value || []);
  const byId = useMemo(() => Object.fromEntries((items || []).map((it) => [it.id, it])), [items]);
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    return (items || [])
      .filter((it) => !selected.has(it.id))
      .filter((it) => (it.name || "").toLowerCase().includes(term))
      .slice(0, 8);
  }, [items, q, selected]);
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next]);
  };
  return (
    <div className="space-y-2" data-testid={testId}>
      {/* Chips of currently-selected items */}
      {value && value.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testId}-chips`}>
          {value.map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => toggle(id)}
              className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-white text-[11px] font-semibold pl-2.5 pr-1.5 h-6 hover:bg-slate-800"
              data-testid={`${testId}-chip-${id}`}
              title="Remove"
            >
              <span className="max-w-[140px] truncate">{byId[id]?.name || id}</span>
              <X className="w-3 h-3 opacity-80" />
            </button>
          ))}
        </div>
      )}
      {/* Search input */}
      <div className="relative">
        <SearchIcon className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={placeholder || "Search…"}
          className="w-full h-9 rounded-lg border border-slate-200 bg-white pl-8 pr-3 text-[12.5px] focus:outline-none focus:border-slate-900"
          data-testid={`${testId}-input`}
        />
      </div>
      {filtered.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid={`${testId}-dropdown`}>
          {filtered.map((it) => (
            <button
              key={it.id}
              type="button"
              onClick={() => { toggle(it.id); setQ(""); }}
              className="w-full text-left px-3 h-8 hover:bg-slate-50 text-[12.5px] text-slate-700 flex items-center gap-2 border-b border-slate-100 last:border-b-0"
              data-testid={`${testId}-option-${it.id}`}
            >
              <Plus className="w-3 h-3 text-slate-400" />
              <span className="truncate">{it.name}</span>
            </button>
          ))}
        </div>
      )}
      {value && value.length === 0 && !q && (
        <div className="text-[10.5px] text-slate-400">No filter · showing everything.</div>
      )}
    </div>
  );
}
